import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {
  EditPatient,
  EditPatientFail,
  EditPatientSuccess,
  LoadManagePatientAccount,
  LoadManagePatientAccountFail,
  LoadManagePatientAccountSuccess,
  LoadSelectedScanAccount,
  LoadSelectedScanAccountFail,
  LoadSelectedScanAccountSuccess,
  ResetManagePatientAccount,
} from './dashboard-clinic-patients-single-account.actions';
import {PROGRESS_STATUSES} from 'src/app/shared/symbols';
import {UserSharedAccountData} from '../../symbols/dashboard-clinic-patients.symbols';
import {BackendError} from '../../../messages/messages.actions';
import {UpdateSingleSubmissionSuccess} from '../../../shared/microservices/submission/submission.actions';
import {Submission} from '../../../dashboard/symbols/submission.symbols';
import {
  clinicPatientsSingleAccountStateDefaults,
  clinicPatientsSingleAccountStateUniqueIdentifier,
  DashboardClinicPatientsSingleAccountStateModel,
} from './dashboard-clinic-patients-single-account.model';
import {GrantAccessForPatientSuccess} from '../dashboard-clinic-patients/dashboard-clinic-patients.actions';
import {DashboardClinicPatientsSingleAccountService} from '../../services/dashboard-clinic-patients-single-account/dashboard-clinic-patients-single-account.service';

@State<DashboardClinicPatientsSingleAccountStateModel>({
  name: clinicPatientsSingleAccountStateUniqueIdentifier,
  defaults: clinicPatientsSingleAccountStateDefaults,
})
@Injectable()
export class DashboardClinicPatientsSingleAccountState {
  constructor(private clinicPatientsSingleAccount: DashboardClinicPatientsSingleAccountService) {}

  @Selector()
  static currentPatient(
    state: DashboardClinicPatientsSingleAccountStateModel
  ): DashboardClinicPatientsSingleAccountStateModel['currentPatient'] {
    return state.currentPatient;
  }

  @Selector()
  static loadPatientStatus(
    state: DashboardClinicPatientsSingleAccountStateModel
  ): DashboardClinicPatientsSingleAccountStateModel['loadPatientStatus'] {
    return state.loadPatientStatus;
  }

  @Selector()
  static managePatientStatus(
    state: DashboardClinicPatientsSingleAccountStateModel
  ): DashboardClinicPatientsSingleAccountStateModel['managePatientStatus'] {
    return state.managePatientStatus;
  }

  @Selector()
  static selectedScanAccount(
    state: DashboardClinicPatientsSingleAccountStateModel
  ): DashboardClinicPatientsSingleAccountStateModel['selectedScanAccount'] {
    return state.selectedScanAccount;
  }

  @Selector()
  static scansIdRelatedToSelectedScan(state: DashboardClinicPatientsSingleAccountStateModel): number[] {
    return state?.selectedScanAccount?.clinic_scans?.map(scan => +scan.id).filter(id => !!id);
  }

  @Selector()
  static loadSelectedScanAccountStatus(
    state: DashboardClinicPatientsSingleAccountStateModel
  ): DashboardClinicPatientsSingleAccountStateModel['loadSelectedScanAccountStatus'] {
    return state.loadSelectedScanAccountStatus;
  }

  /**
   * Update an account with submission inspected data if an account includes such submission.
   */
  static updateAccountWithSubmissionInspectedData(account: UserSharedAccountData, submission: Submission): UserSharedAccountData {
    // Check if the shared related submission exists.
    const userSharedScanIdx = account?.clinic_scans.findIndex(scan => +scan.id === +submission.id);

    // If updated submission is shared ⇒ update its `results_inspected` status in the state.
    if (userSharedScanIdx !== -1) {
      const sharedScans = [...account.clinic_scans];
      sharedScans[userSharedScanIdx] = {
        ...sharedScans[userSharedScanIdx],
        results_inspected: submission.results_inspected,
      };
      account = {...account, clinic_scans: sharedScans};
    }
    return account;
  }

  @Action(EditPatient)
  editPatient(ctx: StateContext<DashboardClinicPatientsSingleAccountStateModel>, {patientId, patientData}: EditPatient): void {
    ctx.patchState({
      managePatientStatus: PROGRESS_STATUSES.IN_PROGRESS,
      lastEditPatientError: null,
    });

    this.clinicPatientsSingleAccount.editPatient(patientId, patientData).subscribe({
      next: updatedPatient => ctx.dispatch(new EditPatientSuccess(patientId, {...patientData, ...updatedPatient})),
      error: error => ctx.dispatch(new EditPatientFail(error)),
    });
  }

  @Action(EditPatientSuccess)
  editPatientSuccess(
    ctx: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {patientId, patientData}: EditPatientSuccess
  ): void {
    // Extract data stored in the state.
    const selectedScanAccount = ctx.getState().selectedScanAccount;

    // Update related stored data if its id matches.
    ctx.patchState({
      managePatientStatus: PROGRESS_STATUSES.SUCCEED,
      selectedScanAccount: selectedScanAccount?.uid === patientId ? {...selectedScanAccount, ...patientData} : selectedScanAccount,
    });
  }

  @Action(EditPatientFail)
  editPatientFail(ctx: StateContext<DashboardClinicPatientsSingleAccountStateModel>, {error}: EditPatientFail): void {
    ctx.patchState({
      managePatientStatus: PROGRESS_STATUSES.INTERRUPTED,
      lastEditPatientError: error,
    });

    ctx.dispatch(new BackendError(error));
  }

  @Action(LoadSelectedScanAccount)
  loadSelectedScanAccount(
    {patchState, dispatch}: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {patientId}: LoadSelectedScanAccount
  ): void {
    patchState({
      loadSelectedScanAccountStatus: PROGRESS_STATUSES.IN_PROGRESS,
      lastLoadSelectedScanAccountError: null,
    });

    this.clinicPatientsSingleAccount.loadAccountDataById(patientId).subscribe({
      next: relatedAccount => dispatch(new LoadSelectedScanAccountSuccess(relatedAccount)),
      error: error => dispatch(new LoadSelectedScanAccountFail(error)),
    });
  }

  @Action(LoadSelectedScanAccountSuccess)
  loadSelectedScanAccountSuccess(
    {patchState}: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {patientData}: LoadSelectedScanAccountSuccess
  ): void {
    patchState({
      loadSelectedScanAccountStatus: PROGRESS_STATUSES.SUCCEED,
      selectedScanAccount: patientData,
    });
  }

  @Action(LoadSelectedScanAccountFail)
  loadSelectedScanAccountFail(
    {dispatch, patchState}: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {error}: LoadSelectedScanAccountFail
  ): void {
    patchState({
      loadSelectedScanAccountStatus: PROGRESS_STATUSES.INTERRUPTED,
      lastLoadSelectedScanAccountError: error,
    });

    dispatch(new BackendError(error));
  }

  @Action(UpdateSingleSubmissionSuccess)
  updateSingleSubmissionSuccess(
    {patchState, getState}: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {submission}: UpdateSingleSubmissionSuccess
  ): void {
    const selectedScanAccount = getState().selectedScanAccount;
    // Check if the updated submission is related to the selected patient.
    if (selectedScanAccount?.uid === submission.user) {
      // Update the patient-related data in the selected patient.
      patchState({
        selectedScanAccount: DashboardClinicPatientsSingleAccountState.updateAccountWithSubmissionInspectedData(
          selectedScanAccount,
          submission
        ),
      });
    }
  }

  @Action(LoadManagePatientAccount)
  loadManagePatientAccount(
    {patchState, dispatch}: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {patientId}: LoadManagePatientAccount
  ): void {
    patchState({
      loadPatientStatus: PROGRESS_STATUSES.IN_PROGRESS,
      lastLoadPatientError: null,
    });

    this.clinicPatientsSingleAccount.loadAccountDataById(patientId).subscribe({
      next: relatedAccount => dispatch(new LoadManagePatientAccountSuccess(relatedAccount)),
      error: error => dispatch(new LoadManagePatientAccountFail(error)),
    });
  }

  @Action(LoadManagePatientAccountSuccess)
  loadManagePatientAccountSuccess(
    {patchState}: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {patientData}: LoadManagePatientAccountSuccess
  ): void {
    patchState({
      loadPatientStatus: PROGRESS_STATUSES.SUCCEED,
      currentPatient: patientData,
    });
  }

  @Action(LoadManagePatientAccountFail)
  loadManagePatientAccountFail(
    {dispatch, patchState}: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {error}: LoadManagePatientAccountFail
  ): void {
    patchState({
      loadPatientStatus: PROGRESS_STATUSES.INTERRUPTED,
      lastLoadPatientError: error,
    });

    dispatch(new BackendError(error));
  }

  @Action(GrantAccessForPatientSuccess)
  grantAccessForPatientSuccess(
    ctx: StateContext<DashboardClinicPatientsSingleAccountStateModel>,
    {patientId, patientData}: GrantAccessForPatientSuccess
  ): void {
    // Extract data stored in the state.
    const selectedScanAccount = ctx.getState().selectedScanAccount;

    // Update related stored data if its id matches.
    if (selectedScanAccount?.uid === patientId) {
      ctx.patchState({
        selectedScanAccount: {...selectedScanAccount, ...patientData},
      });
    }
  }

  @Action(ResetManagePatientAccount)
  resetManagePatientAccount({patchState}: StateContext<DashboardClinicPatientsSingleAccountStateModel>): void {
    patchState({
      loadPatientStatus: PROGRESS_STATUSES.NOT_INITIALIZED,
      managePatientStatus: PROGRESS_STATUSES.NOT_INITIALIZED,
      currentPatient: null,
    });
  }
}
